import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Tag, BarChart3, Euro, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const steps = [
  { icon: Tag, title: 'Code promo personnel', desc: 'Vous recevez un code promo unique et un lien de partage dédié.' },
  { icon: Users, title: 'Partagez avec votre audience', desc: 'Partagez votre code sur vos Reels, TikTok, YouTube, Stories, etc.' },
  { icon: TrendingUp, title: 'Suivi automatique', desc: 'Chaque client utilisant votre code est automatiquement attribué à votre compte.' },
  { icon: Euro, title: 'Commissions versées', desc: 'Vous recevez une commission fixe sur chaque vente générée.' },
];

const benefits = [
  'Commission fixe sur chaque abonnement vendu',
  'Code promo personnalisé pour votre audience',
  'Tableau de bord avec statistiques détaillées',
  'Suivi de vos ventes en temps réel',
  'Demande de paiement directement depuis votre espace',
  'Aucun investissement requis',
];

const rules = [
  'Le code promo est strictement personnel et non transférable.',
  'La commission est versée uniquement sur les ventes réelles et confirmées.',
  'Le programme est incompatible avec le système de parrainage client.',
  'IPTV4KFR se réserve le droit de révoquer un code en cas d\'abus.',
  'Les demandes de paiement sont traitées sous 7 jours ouvrés.',
  'Un montant minimum de 10€ est requis pour demander un virement.',
];

export default function InfluencerProgram() {
  return (
    <div className="min-h-screen" data-testid="influencer-program-page">
      {/* Hero */}
      <section className="py-20 md:py-28 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
              Programme <span className="text-[#FF6B35]">Influenceur</span>
            </h1>
            <p className="text-lg md:text-xl text-[#B8B8BA] max-w-2xl mx-auto leading-relaxed">
              Monétisez votre audience en recommandant IPTV4KFR. Gagnez une commission sur chaque abonnement vendu grâce à votre code promo.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Comment ça fonctionne
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]/40 hover:border-[#FF6B35]/30 transition-all text-center"
                data-testid={`step-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[#FF6B35]/10 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="h-7 w-7 text-[#FF6B35]" />
                </div>
                <div className="text-[#FF6B35] text-xs font-bold uppercase mb-2">Étape {i + 1}</div>
                <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                <p className="text-[#B8B8BA] text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-[#121214]">
        <div className="max-w-3xl mx-auto">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Avantages du programme
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]/40"
                data-testid={`benefit-${i}`}>
                <CheckCircle className="h-5 w-5 text-[#00FF88] shrink-0 mt-0.5" />
                <span className="text-[#B8B8BA] text-sm">{b}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payout process */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Modalités de paiement
          </motion.h2>
          <div className="bg-[#1A1A1D] p-6 md:p-8 rounded-lg border border-[#3A3A3D]/40 space-y-4" data-testid="payout-info">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#FF6B35]/10 flex items-center justify-center shrink-0">
                <span className="text-[#FF6B35] font-bold text-sm">1</span>
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Demande de virement</h4>
                <p className="text-[#B8B8BA] text-sm">Depuis votre espace influenceur, cliquez sur "Demander un virement" et renseignez votre IBAN et le montant souhaité (minimum 10€).</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#00FFFF]/10 flex items-center justify-center shrink-0">
                <span className="text-[#00FFFF] font-bold text-sm">2</span>
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Validation par l'équipe</h4>
                <p className="text-[#B8B8BA] text-sm">Notre équipe vérifie et approuve votre demande sous 48h ouvrées.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#00FF88]/10 flex items-center justify-center shrink-0">
                <span className="text-[#00FF88] font-bold text-sm">3</span>
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Virement effectué</h4>
                <p className="text-[#B8B8BA] text-sm">Le virement est effectué sur votre compte bancaire sous 7 jours ouvrés après approbation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rules */}
      <section className="py-16 px-4 bg-[#121214]">
        <div className="max-w-3xl mx-auto">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Conditions du programme
          </motion.h2>
          <div className="bg-[#1A1A1D] p-6 md:p-8 rounded-lg border border-[#3A3A3D]/40" data-testid="rules">
            <ul className="space-y-3">
              {rules.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-[#B8B8BA] text-sm">
                  <ArrowRight className="h-4 w-4 text-[#FF6B35] shrink-0 mt-0.5" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <BarChart3 className="h-12 w-12 text-[#FF6B35] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Vous êtes déjà influenceur ?</h2>
            <p className="text-[#B8B8BA] mb-8">Connectez-vous pour accéder à votre tableau de bord et suivre vos performances.</p>
            <Link to="/connexion" className="btn-primary py-3 px-8 text-base inline-flex items-center gap-2" data-testid="cta-login">
              Accéder à mon espace <ArrowRight className="h-5 w-5" />
            </Link>
            <p className="text-[#6B6B6E] text-xs mt-4">Le programme est sur invitation uniquement. Contactez-nous pour en faire partie.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
